import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Robust helper to check if an event originated from inside the Sonner toast container.
 * Handles: svg/path elements, Text nodes, composedPath, and direct element checks.
 */
function isInsideToastEvent(event: Event): boolean {
  const TOAST_SELECTORS = '[data-sonner-toaster], [data-sonner-toast]';

  // 1. Try composedPath first (most reliable for shadow DOM and event retargeting)
  const originalEvent = (event as CustomEvent)?.detail?.originalEvent;
  const path = originalEvent?.composedPath?.() ?? (event as PointerEvent).composedPath?.();
  if (path && path.length > 0) {
    for (const node of path) {
      if (node instanceof Element && node.matches(TOAST_SELECTORS)) {
        return true;
      }
    }
  }

  // 2. Get the actual target element
  let target: EventTarget | null = originalEvent?.target ?? event.target;

  // Handle non-Element targets (Text nodes, etc.)
  if (target && !(target instanceof Element)) {
    target = (target as Node).parentElement;
  }

  // 3. Check if target is inside toast using closest()
  if (target instanceof Element) {
    if (target.closest(TOAST_SELECTORS)) {
      return true;
    }
  }

  // 4. Final fallback: check if toast container contains the target
  const toastContainer = document.querySelector('[data-sonner-toaster]');
  if (toastContainer && target instanceof Node && toastContainer.contains(target)) {
    return true;
  }

  return false;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Hide the default overlay (for use with global overlay system) */
    hideOverlay?: boolean;
    /** Hide the default close button (when using custom header with close) */
    hideCloseButton?: boolean;
    /** Accessible title for screen readers (visually hidden if no DialogTitle in children) */
    accessibleTitle?: string;
    /** Accessible description for screen readers (visually hidden) */
    accessibleDescription?: string;
  }
>(
  (
    {
      className,
      children,
      hideOverlay,
      hideCloseButton,
      accessibleTitle,
      accessibleDescription,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      onEscapeKeyDown,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Wrap ALL dismiss-related handlers to ignore clicks on Sonner toasts
    const handleInteractOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onInteractOutside) {
          onInteractOutside(event as Parameters<typeof onInteractOutside>[0]);
        }
      },
      [onInteractOutside]
    );

    const handlePointerDownOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onPointerDownOutside) {
          onPointerDownOutside(event as Parameters<typeof onPointerDownOutside>[0]);
        }
      },
      [onPointerDownOutside]
    );

    const handleFocusOutside = React.useCallback(
      (event: Event) => {
        if (isInsideToastEvent(event)) {
          event.preventDefault();
          return;
        }
        if (onFocusOutside) {
          onFocusOutside(event as Parameters<typeof onFocusOutside>[0]);
        }
      },
      [onFocusOutside]
    );

    // Generate unique IDs for a11y attributes
    const titleId = React.useId();
    const descriptionId = React.useId();

    return (
      <DialogPortal>
        {!hideOverlay && <DialogOverlay />}
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
            className
          )}
          onInteractOutside={handleInteractOutside}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          aria-describedby={ariaDescribedBy !== undefined ? ariaDescribedBy : descriptionId}
          {...props}
        >
          {/* Visually hidden title for screen readers - only if accessibleTitle is explicitly provided
              Consumers should either provide a visible DialogTitle in children OR pass accessibleTitle 
              
              RESEARCH NOTE: Radix checks for DialogTitle in a useEffect. If we conditionally render it,
              Radix's check might run before React renders the title. Testing always rendering it. */}
          <DialogPrimitive.Title className="sr-only">
            {accessibleTitle || 'Dialog'}
          </DialogPrimitive.Title>
          {/* Visually hidden description for screen readers - fixes Radix a11y warning
              Only render if aria-describedby is not explicitly provided by consumer. */}
          {ariaDescribedBy === undefined && (
            <DialogPrimitive.Description className="sr-only" id={descriptionId}>
              {accessibleDescription || accessibleTitle}
            </DialogPrimitive.Description>
          )}
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
